import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  ADMIN_TAB_CONTENT_CONTAINER_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';
import Profile from '../../../assets/img/User.png';

const ViolationCodeDetail = () => {
  const { violationCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('30 Days');
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const timeframeRef = useRef(null);
  
  useClickOutside(timeframeRef, () => setTimeframeOpen(false));
  
  const [violationData, setViolationData] = useState(null);
  
  // Mock trend data for the line graph
  const generateTrendData = (days = 30) => {
    const data = [];
    const baseValue = 40;
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const value = baseValue + Math.floor(Math.random() * 20) - 10;
      data.push({
        date: date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
        value: Math.max(0, value)
      });
    }
    return data;
  };

  useEffect(() => {
    const fetchViolationDetail = async () => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check if violation data was passed via navigation state
        const passedViolation = location.state?.violation;
        
        // Default structure with all required properties
        const defaultData = {
          violationCode: violationCode || 'SCAM_GENERIC',
          name: 'Scam / Fraud Behaviour',
          category: 'Fraud / Scam',
          severity: 'High',
          appliesTo: ['Post', 'Review', 'Message'],
          status: 'Active',
          description: 'This violation fires when off-platform payment requests or suspicious financial patterns are detected in posts, reviews, or messages.',
          policyReference: '/admin/moderation/rules',
          // Metrics based on timeframe
          metrics: {
            '7 Days': {
              totalFlags: 280,
              uniqueItems: 189,
              contentBreakdown: { Post: 50, Review: 30, Message: 20 },
              actionRate: 78,
              escalationRate: 12,
              noActionRate: 4
            },
            '14 Days': {
              totalFlags: 560,
              uniqueItems: 378,
              contentBreakdown: { Post: 50, Review: 30, Message: 20 },
              actionRate: 78,
              escalationRate: 12,
              noActionRate: 4
            },
            '30 Days': {
              totalFlags: 1204,
              uniqueItems: 812,
              contentBreakdown: { Post: 50, Review: 30, Message: 20 },
              actionRate: 78,
              escalationRate: 12,
              noActionRate: 4
            },
            '90 Days': {
              totalFlags: 3612,
              uniqueItems: 2436,
              contentBreakdown: { Post: 50, Review: 30, Message: 20 },
              actionRate: 78,
              escalationRate: 12,
              noActionRate: 4
            }
          },
          // Example cases
          exampleCases: [
            {
              caseId: 'MOD-88941',
              type: 'Message',
              snippet: 'Pay upfront via bank transfer…',
              outcome: 'Escalated (L2)',
              caseLink: '/admin/moderation/queue/MOD-88941'
            },
            {
              caseId: 'MOD-88942',
              type: 'Review',
              snippet: 'He asked for cash first…',
              outcome: 'Content Removed',
              caseLink: '/admin/moderation/queue/MOD-88942'
            },
            {
              caseId: 'MOD-88955',
              type: 'Post',
              snippet: 'Cheap bathroom reno, pay direct…',
              outcome: 'Content Removed',
              caseLink: '/admin/moderation/queue/MOD-88955'
            },
            {
              caseId: 'MOD-88960',
              type: 'Message',
              snippet: 'Send money to this account…',
              outcome: 'Escalated (L2)',
              caseLink: '/admin/moderation/queue/MOD-88960'
            },
            {
              caseId: 'MOD-88965',
              type: 'Review',
              snippet: 'Payment required before service…',
              outcome: 'Content Removed',
              caseLink: '/admin/moderation/queue/MOD-88965'
            }
          ],
          // Rule configuration
          ruleConfiguration: {
            ruleType: 'ML Pattern + Behaviour Threshold',
            triggerLogic: [
              'Off-platform payment patterns detected > 3 times',
              'Suspicious payment keywords',
              'Message-to-review behaviour correlation model v2.3.1'
            ],
            linkedEnforcement: [
              'Auto-hide if severity is High',
              'Auto-escalate if repeated 3 times within 48 hours'
            ]
          },
          trendData: generateTrendData(30)
        };
        
        // Merge passed violation data with default structure to ensure all properties exist
        const mergedData = {
          ...defaultData,
          ...passedViolation,
          // Ensure metrics structure exists even if passed violation doesn't have it
          metrics: passedViolation?.metrics || defaultData.metrics,
          // Ensure other nested structures exist
          exampleCases: passedViolation?.exampleCases || defaultData.exampleCases,
          ruleConfiguration: passedViolation?.ruleConfiguration || defaultData.ruleConfiguration,
          trendData: passedViolation?.trendData || defaultData.trendData
        };
        
        setViolationData(mergedData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching violation detail:', error);
        setLoading(false);
      }
    };
    
    fetchViolationDetail();
  }, [violationCode, location.state]);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    setTimeframeOpen(false);
  };

  const getCurrentMetrics = () => {
    if (!violationData || !violationData.metrics) return null;
    return violationData.metrics[timeframe] || violationData.metrics['30 Days'] || null;
  };

  const formatContentBreakdown = (breakdown) => {
    if (!breakdown) return '—';
    const parts = Object.entries(breakdown)
      .map(([key, value]) => `${key} ${value}%`)
      .join(' / ');
    return parts;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading violation details...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!violationData) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AdminMainText className="text-alertred mb-4">Violation not found</AdminMainText>
            <AdminLinkAction type="button" onClick={() => navigate('/admin/moderation/violations')}>
              Back to flags &amp; violations
            </AdminLinkAction>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const currentMetrics = getCurrentMetrics();

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'description', label: 'Description & Policy' },
    { id: 'metrics', label: 'Metrics & Trend' },
    { id: 'examples', label: 'Example Cases' },
    { id: 'configuration', label: 'Rule Configuration' }
  ];

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>

          {/* A. PAGE HEADER */}
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-primaryblue/10 border border-lightgray flex items-center justify-center">
                  <i className="fi fi-rr-shield-exclamation text-2xl text-primaryblue"></i>
                </div>
                <div>
                  <AdminPanelTitle className="text-offblack">
                    {violationData.violationCode}
                  </AdminPanelTitle>
                  <div className="flex items-center gap-3 mt-2">
                    <AdminMainText className="text-offblack">
                      {violationData.name}
                    </AdminMainText>
                    <AdminStatusBadge status={violationData.status} label={violationData.status} />
                  </div>
                </div>
              </div>
            </div>
          </DashboardMainContainer>

          {/* Tabs */}
          <DashboardMainContainer className="!bg-white">
            <div className="border-b border-lightgray">
              <div className="flex space-x-8 overflow-x-auto">
                {tabs.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className={ADMIN_TAB_CONTENT_CONTAINER_CLASS}>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Violation Code</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{violationData.violationCode}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Name</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{violationData.name}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Category</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{violationData.category}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Severity</AdminDetailFieldLabel>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-white ${
                        violationData.severity === 'Critical'
                          ? 'bg-alertred'
                          : violationData.severity === 'High'
                          ? 'bg-orange-500'
                          : violationData.severity === 'Medium'
                          ? 'bg-warmyellow'
                          : 'bg-freshgreen'
                      }`}>
                        {violationData.severity}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Applies To</AdminDetailFieldLabel>
                      <div className="text-sm text-offblack font-medium">{violationData.appliesTo.join(', ')}</div>
                    </div>
                    <div className="space-y-1">
                      <AdminDetailFieldLabel>Status</AdminDetailFieldLabel>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold text-white ${
                        violationData.status === 'Active'
                          ? 'bg-freshgreen'
                          : violationData.status === 'Testing'
                          ? 'bg-warmyellow'
                          : violationData.status === 'Paused'
                          ? 'bg-orange-500'
                          : 'bg-mediumgray'
                      }`}>
                        {violationData.status}
                      </span>
                    </div>
                  </div>
              )}

              {/* Description & Policy Tab */}
              {activeTab === 'description' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <AdminDetailFieldLabel>Description</AdminDetailFieldLabel>
                      <AdminMainText className="text-offblack">
                        {violationData.description}
                      </AdminMainText>
                    </div>
                    <div className="space-y-2">
                      <AdminDetailFieldLabel>Policy Reference</AdminDetailFieldLabel>
                      <button
                        onClick={() => navigate(violationData.policyReference || '/admin/moderation/rules')}
                        className="text-primaryblue hover:text-offblack text-sm font-medium underline"
                      >
                        View in Rulebook →
                      </button>
                    </div>
                  </div>
              )}

              {/* Metrics & Trend Tab */}
              {activeTab === 'metrics' && (
                  <div className="space-y-6">
                    {/* Timeframe Selector */}
                    <div className="flex items-center justify-between">
                      <AdminPanelTitle className="text-offblack">Metrics & Trend</AdminPanelTitle>
                      <div className="relative" ref={timeframeRef}>
                        <AdminFilterDropdownButton
                          onClick={() => setTimeframeOpen(!timeframeOpen)}
                        >
                          <span>{timeframe}</span>
                          <i
                            className={
                              timeframeOpen
                                ? "fi fi-rs-angle-up lg:text-[12px] cursor-pointer flex items-center ml-3"
                                : "fi fi-rs-angle-down lg:text-[12px] cursor-pointer flex items-center ml-3"
                            }
                          ></i>
                        </AdminFilterDropdownButton>
                        {timeframeOpen && (
                          <div className="flex flex-col rounded w-36 py-2 absolute z-30 bg-white text-offblack mt-1 shadow-lg border border-lightgray right-0">
                            {['7 Days', '14 Days', '30 Days', '90 Days'].map((tf) => (
                              <AdminFilterMenuItem
                                key={tf}
                                active={timeframe === tf}
                                onClick={() => handleTimeframeChange(tf)}
                              >
                                {tf}
                              </AdminFilterMenuItem>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    {currentMetrics ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Total Flags</AdminDetailFieldLabel>
                            <div className="text-2xl text-offblack font-semibold">{currentMetrics.totalFlags?.toLocaleString() || '0'}</div>
                          </div>
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Unique Items</AdminDetailFieldLabel>
                            <div className="text-2xl text-offblack font-semibold">{currentMetrics.uniqueItems?.toLocaleString() || '0'}</div>
                          </div>
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Content Breakdown</AdminDetailFieldLabel>
                            <div className="text-sm text-offblack font-medium">{formatContentBreakdown(currentMetrics.contentBreakdown)}</div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Action Rate</AdminDetailFieldLabel>
                            <div className="text-2xl text-offblack font-semibold">{currentMetrics.actionRate || 0}%</div>
                          </div>
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>Escalation Rate</AdminDetailFieldLabel>
                            <div className="text-2xl text-offblack font-semibold">{currentMetrics.escalationRate || 0}%</div>
                          </div>
                          <div className="space-y-1">
                            <AdminDetailFieldLabel>No Action Rate</AdminDetailFieldLabel>
                            <div className="text-2xl text-offblack font-semibold">{currentMetrics.noActionRate || 0}%</div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-offblack opacity-50">
                        Metrics not available
                      </div>
                    )}

                    {/* Trend Graph */}
                    <div className="space-y-3 mt-6">
                      <AdminDetailFieldLabel>Trend (Last 30 Days)</AdminDetailFieldLabel>
                      <div className="h-48 border border-lightgray rounded-lg p-4 flex items-end justify-between gap-0.5 bg-gray-50 relative">
                        {violationData.trendData && violationData.trendData.length > 0 ? (
                          <>
                            {violationData.trendData.map((point, index) => {
                              const maxValue = Math.max(...violationData.trendData.map(p => p.value));
                              const height = maxValue > 0 ? (point.value / maxValue) * 100 : 0;
                              return (
                                <div
                                  key={index}
                                  className="flex-1 flex flex-col items-center justify-end group relative"
                                  title={`${point.date}: ${point.value} flags`}
                                >
                                  <div
                                    className="w-full bg-primaryblue rounded-t hover:bg-brightblue transition-all cursor-pointer group-hover:opacity-80"
                                    style={{ height: `${height}%`, minHeight: '2px' }}
                                  ></div>
                                  {index % 7 === 0 && (
                                    <span className="text-[9px] text-offblack opacity-60 mt-1 transform -rotate-45 origin-top-left whitespace-nowrap absolute -bottom-4 left-0">
                                      {point.date.split(' ')[0]}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                            {/* Y-axis labels */}
                            <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-[9px] text-offblack opacity-50 pr-2">
                              {(() => {
                                const maxValue = Math.max(...violationData.trendData.map(p => p.value));
                                return [maxValue, Math.floor(maxValue * 0.75), Math.floor(maxValue * 0.5), Math.floor(maxValue * 0.25), 0].map((val, idx) => (
                                  <span key={idx}>{val}</span>
                                ));
                              })()}
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-offblack opacity-50 text-sm">
                            No trend data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
              )}

              {/* Example Cases Tab */}
              {activeTab === 'examples' && (
                <div>
                  <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                    <div className="overflow-hidden">
                      <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(100px, 1fr) minmax(200px, 2fr) minmax(150px, 1.5fr) minmax(100px, 1fr)' }}>
                        <div className="flex items-center">
                          <span className="font-semibold">Case ID</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-semibold">Type</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-semibold">Snippet</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-semibold">Outcome</span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-semibold">Action</span>
                        </div>
                      </div>
                      
                      {violationData.exampleCases && violationData.exampleCases.length > 0 ? (
                        violationData.exampleCases.map((example, idx) => (
                          <div 
                            key={idx} 
                            className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4" 
                            style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(100px, 1fr) minmax(200px, 2fr) minmax(150px, 1.5fr) minmax(100px, 1fr)' }}
                          >
                            <div className="flex items-center">
                              <span className="">{example.caseId}</span>
                            </div>
                            <div className="flex items-center">
                              <span className="">{example.type}</span>
                            </div>
                            <div className="flex items-center min-w-0">
                              <span className="truncate" title={example.snippet}>
                                {example.snippet}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className={`px-2 py-1 rounded-full text-xs text-white ${
                                example.outcome.includes('Escalated')
                                  ? 'bg-alertred'
                                  : example.outcome.includes('Removed')
                                  ? 'bg-orange-500'
                                  : 'bg-mediumgray'
                              }`}>
                                {example.outcome}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <button
                                onClick={() => navigate(example.caseLink)}
                                className="text-primaryblue hover:text-offblack text-sm font-medium"
                              >
                                Open →
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-8 text-center">
                          <span className="text-mediumgray">No example cases available</span>
                        </div>
                      )}
                    </div>
                  </DashboardMainContainer>
                </div>
              )}

              {/* Rule Configuration Tab */}
              {activeTab === 'configuration' && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <AdminDetailFieldLabel>Rule Type</AdminDetailFieldLabel>
                        <div className="text-sm text-offblack font-medium">{violationData.ruleConfiguration.ruleType}</div>
                      </div>
                      
                      <div className="space-y-2">
                        <AdminDetailFieldLabel>Trigger Logic</AdminDetailFieldLabel>
                        <ul className="list-disc list-inside space-y-1">
                          {violationData.ruleConfiguration.triggerLogic.map((logic, idx) => (
                            <li key={idx} className="text-sm text-offblack">
                              {logic}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="space-y-2">
                        <AdminDetailFieldLabel>Linked Enforcement</AdminDetailFieldLabel>
                        <ul className="list-disc list-inside space-y-1">
                          {violationData.ruleConfiguration.linkedEnforcement.map((enforcement, idx) => (
                            <li key={idx} className="text-sm text-offblack">
                              {enforcement}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
              )}
            </div>
          </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ViolationCodeDetail;
